import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Flatpickr from "react-flatpickr";
import BreadCrumb from 'Common/BreadCrumb';
import { Link } from 'react-router-dom';
import TableContainer from 'Common/TableContainer';
import moment from "moment";
import Select from 'react-select';

// Icons
import { Search, CheckCircle, Loader, X } from 'lucide-react';
import Modal from 'Common/Components/Modal';
import DeleteModal from 'Common/DeleteModal';

// Images

// react-redux
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';

// Formik
import * as Yup from "yup";
import { useFormik } from "formik";

import {
  onDeleteService,
  onGetBookingList,
  onUpdateBooking,
  onAddBooking,
} from 'slices/thunk';
import { ToastContainer } from 'react-toastify';
import filterDataBySearch from 'Common/filterDataBySearch';
import { currencyFormatter } from '../../helpers/formatter';
import { Booking, Service, doGetAttendedBy, doListServices } from '../../services/booking.service';
import CreatableSelect from 'react-select/creatable';
import { CustomerModel, doListCustomer } from '../../services/customer.service';
import { AxiosResponse } from 'axios';
import { User } from '../../services/user.service';
import BookingDetailModal from './BookingDetailModal';
import { PaymentStatusHelper } from '../../Common/Components/Status';
import { doGetProjectSetting, getAuthProject, Project } from '../../services/project.service';

const Bookings = () => {
  const dispatch = useDispatch<any>();

  const selectDataList = createSelector(
    (state: any) => state.Booking,
    (booking) => ({
      bookingList: booking.bookingList
    })
  );

  const { bookingList } = useSelector(selectDataList);
  const [booking, setBooking] = useState<any>([]);
  const [eventData, setEventData] = useState<any>();

  const [show, setShow] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const [serviceList, setServiceList] = useState<Service[]>([])
  const [serviceDroplist, setServiceDroplist] = useState<Array<{ value: string, label: string }>>([])

  const [customerList, setCustomerList] = useState<CustomerModel[]>([])
  const [customerDroplist, setCustomerDropList] = useState<Array<{ value: string, label: string }>>([])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [attendedByList, setAttendedByList] = useState<User[]>([])
  const [attendedByDroplist, setAttendedByDroplist] = useState<Array<{ value: string, label: string }>>([])

  const [openDetailModal, setOpenDetailModal] = useState<boolean>(false)
  const [detailBooking, setDetailBooking] = useState<Booking>()
  const project = getAuthProject()
  const [projectSetting, setProjectSetting] = useState<Project>()

  // Get Data
  useEffect(() => {
    dispatch(onGetBookingList())
  }, [dispatch]);

  useEffect(() => {
    setBooking(bookingList)
  }, [bookingList]);

  useEffect(() => {
    handleListAllServices()
    handleListAllCustomers()
    handleListAttendedBy()
  }, []);

  useEffect(() => {
    doGetProjectSetting().then((res: any) => {
      const projectSetting = res as Project
      setProjectSetting(projectSetting)
    })
  }, [])

  // Delete Modal
  const [deleteModal, setDeleteModal] = useState<boolean>(false)
  const deleteToggle = () => setDeleteModal(!deleteModal)

  const handleListAttendedBy = () => {
    doGetAttendedBy().then((res) => {
      const users = res as unknown as User[]
      setAttendedByList(users)
      setAttendedByDroplist(users.map((user: User) => {
        return {
          value: user.userId,
          label: `${user.role} - ${user.name}`
        }
      }))
    })
  }

  const handleListAllServices = () => {
    doListServices(0).then((res) => {
      const services = res as unknown as Service[]
      setServiceList(services)
      setServiceDroplist(services.map((service: Service) => {
        return {
          value: service.serviceId,
          label: `${service.code} - ${service.title}`
        }
      }))
    })
  }

  const handleListAllCustomers = () => {
    doListCustomer(0).then((res) => {
      const response = res as AxiosResponse
      const customers = response.data
      setCustomerList(customers)
      setCustomerDropList(customers.map((customer: CustomerModel) => {
        return {
          value: customer.customerId,
          label: customer.name
        }
      }))
    })
  }

  const handleChangeService = (selectedOption: any) => {
    const service = serviceList.find((service: Service) => service.serviceId === selectedOption.value)
    if (service) {
      validation.setFieldValue(`serviceId`, service.serviceId)
      validation.setFieldValue(`totalPrice`, service.price)
      validation.setFieldValue(`reservationDuration`, service.unitValue)
    }
  }

  const handleChangeCustomer = (selectedOption: any) => {
    if (selectedOption?.value) {
      const customer = customerList.find((customer: CustomerModel) => customer.customerId === selectedOption.value)
      if (customer) {
        validation.setFieldValue('customerId', customer?.customerId)
        validation.setFieldValue('customerName', customer?.name)
        validation.setFieldValue('customerAddress', customer?.address)
        validation.setFieldValue('customerPhoneNo', customer?.phoneNo)
      }
    }
  }

  const handleChangeAttendedBy = (selectedOption: any) => {
    if (selectedOption?.value) {
      validation.setFieldValue('attendedBy', selectedOption.value)
    }
  }

  const handleDelete = () => {
    if (eventData) {
      dispatch(onDeleteService(eventData.serviceBookingId));
      setDeleteModal(false);
    }
  };

  // Update Data
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleUpdateDataClick = (ele: any) => {
    setEventData({ ...ele });
    setIsEdit(true);
    setShow(true);
  };

  // validation
  const validation: any = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      serviceBookingId: (eventData && eventData.serviceBookingId) || '',
      serviceId: (eventData && eventData.serviceId) || '',
      customerId: (eventData && eventData.customerId) || '',
      reservationDateTime: (eventData && eventData.reservationDateTime) || '',
      reservationEndDateTime: (eventData && eventData.reservationEndDateTime) || '',
      reservationDuration: (eventData && eventData.reservationDuration) || '',
      totalPrice: (eventData && eventData.totalPrice) || '',
      paymentStatus: (eventData && eventData.paymentStatus) || 'unpaid',
      paymentMethod: (eventData && eventData.paymentMethod) || '',
      attendedBy: (eventData && eventData.attendedBy) || '',
      isExternal: (eventData && eventData.isExternal) || false,
      attendedAddress: (eventData && eventData.attendedAddress) || '',
      remark: (eventData && eventData.remark) || '',
      status: (eventData && eventData.status) || '',
    },
    validationSchema: Yup.object({
      serviceId: Yup.string().required("Service is required"),
      customerId: Yup.string().required("Customer is required"),
      reservationDateTime: Yup.string().required("Reservation Date Time is required"),
      reservationEndDateTime: Yup.string().optional(),
      reservationDuration: Yup.string().optional(),
      totalPrice: Yup.string().required("Total Price is required"),
      paymentStatus: Yup.string().optional(),
      paymentMethod: Yup.string().optional(),
      attendedBy: Yup.string().required("Attended By is required"),
      isExternal: Yup.boolean().default(false),
      attendedAddress: Yup.string().optional(),
      remark: Yup.string().optional(),
      status: Yup.string().optional(),
    }),

    onSubmit: (values) => {
      const data = {
        ...values,
      }
      if (isEdit) {
        delete data.reservationEndDateTime
        dispatch(onUpdateBooking(data))
      } else {
        delete data.serviceBookingId
        dispatch(onAddBooking(data))
      }
      toggle();
    },
  });

  //
  const toggle = useCallback(() => {
    if (show) {
      setShow(false);
      setEventData("");
      setIsEdit(false);
    } else {
      setShow(true);
      setEventData("");
      validation.resetForm();
    }
  }, [show, validation]);

  // Search Data
  const filterSearchData = (e: any) => {
    const search = e.target.value;
    const keysToSearch = ['attendedBy'];
    filterDataBySearch(bookingList, search, keysToSearch, setBooking);
  };

  // columns
  const Status = ({ item }: any) => {
    switch (item) {
      case "completed":
        return (<span className="px-2.5 py-0.5 text-xs font-medium rounded border bg-green-100 border-transparent text-green-500 dark:bg-green-500/20 dark:border-transparent inline-flex items-center status"><CheckCircle className="w-3 h-3 mr-1.5" />{item}</span>);
      case "confirmed":
        return (<span className="px-2.5 py-0.5 text-xs font-medium rounded border bg-green-100 border-transparent text-green-500 dark:bg-green-500/20 dark:border-transparent inline-flex items-center status"><CheckCircle className="w-3 h-3 mr-1.5" />{item}</span>);
      case "pending":
        return (<span className="px-2.5 py-0.5 inline-flex items-center text-xs font-medium rounded border bg-slate-100 border-transparent text-slate-500 dark:bg-slate-500/20 dark:text-zink-200 dark:border-transparent status"><Loader className="w-3 h-3 mr-1.5" />{item}</span>);
      case "cancelled":
        return (<span className="px-2.5 py-0.5 inline-flex items-center text-xs font-medium rounded border bg-red-100 border-transparent text-red-500 dark:bg-red-500/20 dark:border-transparent status"><X className="w-3 h-3 mr-1.5" />{item}</span>);
      default:
        return (<span className="px-2.5 py-0.5 text-xs font-medium rounded border bg-green-100 border-transparent text-green-500 dark:bg-green-500/20 dark:border-transparent inline-flex items-center status"><CheckCircle className="w-3 h-3 mr-1.5" />{item}</span>);
    }
  }

  const columns = useMemo(() => [
    {
      header: (
        <div className="flex items-center h-full">
          <input id="CheckboxAll" className="w-4 h-4 bg-white border border-slate-200 checked:bg-none dark:bg-zink-700 dark:border-zink-500 rounded-sm appearance-none arrow-none relative after:absolute after:content-['\eb7b'] after:top-0 after:left-0 after:font-remix after:leading-none after:opacity-0 checked:after:opacity-100 after:text-custom-500 checked:border-custom-500 dark:after:text-custom-500 dark:checked:border-custom-800 cursor-pointer" type="checkbox" />
        </div>
      ),
      enableSorting: false,
      id: "checkAll",
      cell: (cell: any) => {
        return (
          <div className="flex items-center h-full">
            <input id="Checkbox1" className="w-4 h-4 bg-white border border-slate-200 checked:bg-none dark:bg-zink-700 dark:border-zink-500 rounded-sm appearance-none arrow-none relative after:absolute after:content-['\eb7b'] after:top-0 after:left-0 after:font-remix after:leading-none after:opacity-0 checked:after:opacity-100 after:text-custom-500 checked:border-custom-500 dark:after:text-custom-500 dark:checked:border-custom-800 cursor-pointer" type="checkbox" />
          </div>
        );
      }
    },
    {
      header: "Service",
      accessorKey: "serviceId",
      enableColumnFilter: false,
      cell: (cell: any) => (
        <Link to="#" onClick={
          () => {
            const data = cell.row.original
            setDetailBooking(data)
            setOpenDetailModal(true)
          }
        }>
          <span className='font-black'>{cell.row.original.service?.title}</span>
        </Link>
      )
    },
    {
      header: "Reservation DateTime",
      accessorKey: "reservationDateTime",
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div className="flex items-center gap-2">
          <div className="grow">
            <h6 className="mb-1">{moment(cell.getValue()).format('DD MMM YYYY')}</h6>
            <span className="mb-1">{moment(cell.getValue()).format('HH:mm A')} - {moment(cell.row.original.reservationEndDateTime).format('HH:mm A')}</span>
          </div>
        </div>
      ),
    },
    {
      header: "Duration",
      accessorKey: "reservationDuration",
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div className="flex items-center gap-2">
          <div className="grow">
            <span className="mb-1">{cell.getValue() ? cell.getValue() + ' minutes' : '-'}</span>
          </div>
        </div>
      ),
    },
    {
      header: "Customer",
      accessorKey: "customerId",
      enableColumnFilter: false,
      cell: (cell: any) => (
        <span>{cell.row.original.customer?.name}</span>
      )
    },
    {
      header: "Attended By",
      accessorKey: "attendedBy",
      enableColumnFilter: false,
      cell: (cell: any) => (
        <span>{cell.row.original.attendedByUser?.name || '-'}</span>
      )
    },
    {
      header: "Reserved At",
      accessorKey: "createdAt",
      enableColumnFilter: false,
      cell: (cell: any) => (
        <span>{moment(cell.getValue()).format("DD MMM YYYY")}</span>
      ),
    },
    {
      header: "Status",
      accessorKey: "status",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (cell: any) => (
        <Status item={cell.getValue()} />
      ),
    },
    {
      header: "Payment Status",
      accessorKey: "paymentStatus",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (cell: any) => (
        <PaymentStatusHelper status={cell.getValue()} />
      ),
    },
    // {
    //   header: "Action",
    //   enableColumnFilter: false,
    //   enableSorting: true,
    //   cell: (cell: any) => (
    //     <Dropdown className="relative">
    //       <Dropdown.Trigger className="flex items-center justify-center w-[30px] h-[30px] p-0 text-slate-500 btn bg-slate-100 hover:text-white hover:bg-slate-600 focus:text-white focus:bg-slate-600 focus:ring focus:ring-slate-100 active:text-white active:bg-slate-600 active:ring active:ring-slate-100 dark:bg-slate-500/20 dark:text-slate-400 dark:hover:bg-slate-500 dark:hover:text-white dark:focus:bg-slate-500 dark:focus:text-white dark:active:bg-slate-500 dark:active:text-white dark:ring-slate-400/20" id={cell.row.original.serviceBookingId}>
    //         <MoreHorizontal className="w-3 h-3" />
    //       </Dropdown.Trigger>
    //       <Dropdown.Content placement="right-end" className="absolute z-50 py-2 mt-1 ltr:text-left rtl:text-right list-none bg-white rounded-md shadow-md min-w-[10rem] dark:bg-zink-600" aria-labelledby={cell.row.original.serviceBookingId}>
    //         <li>
    //           <Link data-modal-target="addUserModal" className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200" to="#!"
    //             onClick={() => {
    //               const data = cell.row.original;
    //               handleUpdateDataClick(data);
    //             }}>
    //             <FileEdit className="inline-block w-3 h-3 ltr:mr-1 rtl:ml-1" /> <span className="align-middle">Edit</span></Link>
    //         </li>
    //       </Dropdown.Content>
    //     </Dropdown>
    //   ),
    // }
  ], []
  );

  const statusOptions = [
    { value: '', label: 'Select Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'completed', label: 'Completed' },
  ]

  const paymentMethodOptions = [
    { value: '', label: 'Select Payment Method' },
    { value: 'cash', label: 'Cash' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
  ]

  const paymentStatusOptions = [
    { value: 'paid', label: 'Paid' },
    { value: 'unpaid', label: 'Unpaid' },
  ]

  const handleChange = (selectedOption: any) => {
    if (selectedOption.value === '' || selectedOption.value === 'Hidden') {
      setBooking(bookingList);
    } else {
      const filteredBookings = bookingList.filter((data: any) => data.status === selectedOption.value);
      setBooking(filteredBookings);
    }
  };

  return (
    <React.Fragment>
      <BreadCrumb title='Bookings' pageTitle='Bookings' />
      <DeleteModal show={deleteModal} onHide={deleteToggle} onDelete={handleDelete} />
      <ToastContainer closeButton={false} limit={1} />
      <BookingDetailModal
        booking={detailBooking}
        setBooking={setDetailBooking}
        isOpen={openDetailModal}
        setIsOpen={setOpenDetailModal}
        project={project}
        projectSetting={projectSetting}
      />
      <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12">
        <div className="xl:col-span-12">
          <div className="card" id="usersTable">
            <div className="card-body">
              <div className="flex items-center">
                <h6 className="text-15 grow">Booking List</h6>
                {/* <div className="shrink-0">
                  <button type="button" className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20" onClick={toggle}><Plus className="inline-block w-4 h-4" />
                    <span className="align-middle">Add Booking</span>
                  </button>
                </div> */}
              </div>
            </div>
            <div className="!py-3.5 card-body border-y border-dashed border-slate-200 dark:border-zink-500">
              <form action="#!">
                <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
                  <div className="relative xl:col-span-2">
                    <input type="text" className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="Search for name, email, phone number etc..." autoComplete="off" onChange={(e) => filterSearchData(e)} />
                    <Search className="inline-block w-4 h-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
                  </div>
                  <div className="xl:col-span-2">
                    <Select
                      className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      options={statusOptions}
                      isSearchable={false}
                      defaultValue={statusOptions[0]}
                      onChange={(event: any) => handleChange(event)}
                      id="choices-single-default"
                    />
                  </div>
                </div>
              </form>
            </div>
            <div className="card-body">
              {booking && booking.length > 0 ?
                <TableContainer
                  isPagination={true}
                  columns={(columns || [])}
                  data={(booking || [])}
                  customPageSize={10}
                  divclassName="-mx-5 -mb-5 overflow-x-auto"
                  tableclassName="w-full border-separate table-custom border-spacing-y-1 whitespace-nowrap"
                  theadclassName="text-left relative rounded-md bg-slate-100 dark:bg-zink-600 after:absolute ltr:after:border-l-2 rtl:after:border-r-2 ltr:after:left-0 rtl:after:right-0 after:top-0 after:bottom-0 after:border-transparent [&.active]:after:border-custom-500 [&.active]:bg-slate-100 dark:[&.active]:bg-zink-600"
                  thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold"
                  tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5"
                  PaginationClassName="flex flex-col items-center mt-8 md:flex-row"
                />
                :
                (<div className="noresult">
                  <div className="py-6 text-center">
                    <Search className="w-6 h-6 mx-auto text-sky-500 fill-sky-100 dark:sky-500/20" />
                    <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
                    <p className="mb-0 text-slate-500 dark:text-zink-200">We've searched more than 199+ users We did not find any users for you search.</p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>

      <Modal show={show} onHide={toggle} id="defaultModal" modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600">
        <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-zink-300/20"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500">
          <Modal.Title className="text-16">{!!isEdit ? "Edit Booking" : "Add Booking"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <form action="#!" onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}>
            <div className="mb-3">
              <label htmlFor="serviceInput" className="inline-block mb-2 text-base font-medium">Service</label>
              <Select
                aria-labelledby="aria-label"
                inputId="aria-example-input"
                name={`serviceId`}
                options={serviceDroplist}
                onChange={(selectedOption) => handleChangeService(selectedOption)}
                value={serviceDroplist.find(option => { return option.value === validation.values.serviceId })}
              />
              {validation.touched.serviceId && validation.errors.serviceId ? (
                <p className="text-red-400">{validation.errors.serviceId}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="titleInput" className="inline-block mb-2 text-base font-medium">Attended By</label>
              <Select
                aria-labelledby="aria-label"
                inputId="aria-example-input"
                name={`attendedBy`}
                options={attendedByDroplist}
                onChange={(selectedOption) => handleChangeAttendedBy(selectedOption)}
                value={attendedByDroplist.find(option => { return option.value === validation.values.attendedBy })}
              />
              {validation.touched.attendedBy && validation.errors.attendedBy ? (
                <p className="text-red-400">{validation.errors.attendedBy}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="codeInput" className="inline-block mb-2 text-base font-medium">Reservation Date</label>
              <Flatpickr
                id="reservationDateInput"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                options={{
                  enableTime: true,
                  dateFormat: "d M, Y H:i",
                }}
                placeholder='Select Date' name='reservationDateTime'
                onChange={(date: any) => {
                  validation.setFieldValue("reservationDateTime", date[0])
                }}
                value={validation.values.reservationDateTime || ''}
              />
              {validation.touched.reservationDateTime && validation.errors.reservationDateTime ? (
                <p className="text-red-400">{validation.errors.reservationDateTime}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="customerInput" className="inline-block mb-2 text-base font-medium">Customer</label>
              <CreatableSelect isClearable
                name="customerId"
                options={customerDroplist}
                onChange={(selectedOption) => handleChangeCustomer(selectedOption)}
                value={customerDroplist.find(option => option.value === validation.values.customerId)}
                isDisabled={isEdit}
              />
              {validation.touched.customerId && validation.errors.customerId ? (
                <p className="text-red-400">{validation.errors.customerId}</p>
              ) : null}
            </div>
            <div className="mb-3 flex">
              <label htmlFor="isExternalInput" className="inline-block mb-2 text-base font-medium">External?</label>
              <input type="checkbox" id="isExternalInput" className="ml-4 w-4 h-4 bg-white border border-slate-200 checked:bg-none dark:bg-zink-700 dark:border-zink-500 rounded-sm appearance-none arrow-none relative after:absolute after:content-['\eb7b'] after:top-0 after:left-0 after:font-remix after:leading-none after:opacity-0 checked:after:opacity-100 after:text-custom-500 checked:border-custom-500 dark:after:text-custom-500 dark:checked:border-custom-800 cursor-pointer"
                name="isExternal"
                onChange={validation.handleChange}
                checked={validation.values.isExternal || false}
              />
              {validation.touched.isExternal && validation.errors.isExternal ? (
                <p className="text-red-400">{validation.errors.isExternal}</p>
              ) : null}
            </div>
            {
              validation.values.isExternal && (
                <div className="mb-3">
                  <label htmlFor="attendedAddressInput" className="inline-block mb-2 text-base font-medium">Attended Address</label>
                  <input type="text" id="attendedAddressInput" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                    placeholder="Enter Address"
                    name="attendedAddress"
                    onChange={validation.handleChange}
                    value={validation.values.attendedAddress || ""}
                  />
                  {validation.touched.attendedAddress && validation.errors.attendedAddress ? (
                    <p className="text-red-400">{validation.errors.attendedAddress}</p>
                  ) : null}
                </div>
              )
            }
            <div className="mb-3">
              <label htmlFor="remarkInput" className="inline-block mb-2 text-base font-medium">Remark</label>
              <input type="text" id="remarkInput" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Enter Remark"
                name="remark"
                onChange={validation.handleChange}
                value={validation.values.remark || ""}
              />
              {validation.touched.remark && validation.errors.remark ? (
                <p className="text-red-400">{validation.errors.remark}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="paymentMethodInput" className="inline-block mb-2 text-base font-medium">Payment Method</label>
              <Select
                aria-labelledby="aria-label"
                inputId="aria-example-input"
                name={`paymentMethod`}
                options={paymentMethodOptions}
                onChange={(selectedOption) => validation.setFieldValue(`paymentMethod`, selectedOption?.value)}
                value={paymentMethodOptions.find(option => { return option.value === validation.values.paymentMethod })}
              />
              {validation.touched.paymentMethod && validation.errors.paymentMethod ? (
                <p className="text-red-400">{validation.errors.paymentMethod}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="paymentStatusInput" className="inline-block mb-2 text-base font-medium">Payment Status</label>
              <Select
                aria-labelledby="aria-label"
                inputId="aria-example-input"
                name={`paymentStatus`}
                options={paymentStatusOptions}
                onChange={(selectedOption) => validation.setFieldValue(`paymentStatus`, selectedOption?.value)}
                value={paymentStatusOptions.find(option => { return option.value === validation.values.paymentStatus })}
              />
              {validation.touched.paymentStatus && validation.errors.paymentStatus ? (
                <p className="text-red-400">{validation.errors.paymentStatus}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="totalPriceInput" className="inline-block mb-2 text-base font-medium">Duration</label>
              <p className=''>{validation.values.reservationDuration ? `${validation.values.reservationDuration} Minutes` : '-'}</p>
            </div>
            <div className="mb-3">
              <label htmlFor="totalPriceInput" className="inline-block mb-2 text-base font-bold">Total Price</label>
              <p className='font-bold'>{currencyFormatter(validation.values.totalPrice || 0)}</p>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button type="reset" data-modal-close="addDocuments" className="text-red-500 transition-all duration-200 ease-linear bg-white border-white btn hover:text-red-600 focus:text-red-600 active:text-red-600 dark:bg-zink-500 dark:border-zink-500" onClick={toggle}>Cancel</button>
              <button type="submit" className="text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20">
                {!!isEdit ? "Update Booking" : "Add Booking"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default Bookings;
